import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Products";
import { Mask } from "@/utils/mask";
import {
  setStatusProducts,
  getShowProducts,
  deleteProductsData,
} from "@/Api/products";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

type SortOrder = "asc" | "desc";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const [sortField, setSortField] = useState<keyof TableDataProps | "">("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const { hasPermission } = usePermissions();
  const handleSort = (field: keyof TableDataProps) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortField) return dataApi?.data || [];
    const sorted = [...(dataApi?.data || [])].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === "lottery") {
        valA = (a.lottery?.name || "") as string;
        valB = (b.lottery?.name || "") as string;
      }

      const strA =
        typeof valA === "number" ? valA.toString() : (valA || "").toString();
      const strB =
        typeof valB === "number" ? valB.toString() : (valB || "").toString();

      if (strA < strB) return sortOrder === "asc" ? -1 : 1;
      if (strA > strB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [dataApi?.data, sortField, sortOrder]);

  const renderSortIndicator = (field: keyof TableDataProps) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? " ▲" : " ▼";
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead onClick={() => handleSort("lottery")}>
              Loteria{renderSortIndicator("lottery")}
            </TableHead>
            <TableHead onClick={() => handleSort("name")}>
              Nome{renderSortIndicator("name")}
            </TableHead>
            <TableHead onClick={() => handleSort("game_quantity")}>
              Jogos{renderSortIndicator("game_quantity")}
            </TableHead>
            <TableHead onClick={() => handleSort("dez_quantity")}>
              Dezenas{renderSortIndicator("dez_quantity")}
            </TableHead>
            <TableHead onClick={() => handleSort("quota_quantity")}>
              Cotas{renderSortIndicator("quota_quantity")}
            </TableHead>
            <TableHead onClick={() => handleSort("total_with_tariff")}>
              Valor Total{renderSortIndicator("total_with_tariff")}
            </TableHead>
            <TableHead onClick={() => handleSort("sale_price")}>
              Preço de Venda{renderSortIndicator("sale_price")}
            </TableHead>
            <TableHead onClick={() => handleSort("price_without_tariff")}>
              Preço sem Tarifa{renderSortIndicator("price_without_tariff")}
            </TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell>
                  {item?.lottery?.name ? (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium text-white uppercase"
                      style={{ backgroundColor: item.lottery.color || "#6b7280" }}
                    >
                      {item.lottery.name}
                    </span>
                  ) : (
                    "Não informado"
                  )}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell>{item?.game_quantity}</TableCell>
                <TableCell>{item?.dez_quantity}</TableCell>
                <TableCell>{item?.quota_quantity}</TableCell>
                <TableCell>
                  {Mask.formatCurrency(item?.total_with_tariff)}
                </TableCell>
                <TableCell>{Mask.formatCurrency(item?.sale_price)}</TableCell>
                <TableCell>
                  {Mask.formatCurrency(item?.price_without_tariff)}
                </TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={Boolean(item?.is_active)}
                    params={{
                      func: setStatusProducts,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: values.is_active ? 0 : 1,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-product")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-product")}>
                      <DeleteBtn
                        params={{
                          func: deleteProductsData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-product-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Produto" }}
                        params={{
                          func: getShowProducts,
                          funcParams: item?.uuid,
                        }}
                        className="sm:max-w-[600px]"
                        render={(responseApi: TableDataProps) => (
                          <div className="w-full h-auto">
                            <div className="space-y-3">
                              <h3 className="text-lg font-medium">
                                Informações Básicas
                              </h3>
                              <div className="grid grid-cols-2 gap-4">
                                <Grid.Item name="Loteria" flex={0}>
                                  {responseApi?.lottery?.name}
                                </Grid.Item>
                                <Grid.Item name="Nome" flex={0}>
                                  {responseApi?.name}
                                </Grid.Item>
                              </div>
                              {responseApi?.created_by?.name && (
                                <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                                  <div className="text-sm space-y-1">
                                    <div className="font-medium text-gray-700 dark:text-gray-300">
                                      Criador
                                    </div>
                                    {responseApi?.created_by?.name && (
                                      <div className="text-gray-600 dark:text-gray-400">
                                        <span className="font-medium">
                                          Produto criado por:
                                        </span>{" "}
                                        {responseApi?.created_by?.name}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              <div className="grid grid-cols-3 gap-4 border-t pt-4 mt-4">
                                <Grid.Item name="Qtd. Jogos" flex={0}>
                                  {responseApi?.game_quantity}
                                </Grid.Item>
                                <Grid.Item name="Qtd. Dezenas" flex={0}>
                                  {responseApi?.dez_quantity}
                                </Grid.Item>
                                <Grid.Item name="Qtd. de Cotas" flex={0}>
                                  {responseApi?.quota_quantity}
                                </Grid.Item>
                              </div>
                              <div className="border-t pt-4 mt-4">
                                <h3 className="text-lg font-medium mb-4">
                                  Cálculo do Preço
                                </h3>
                                <div className="grid grid-cols-3 gap-4">
                                  <Grid.Item name="Total sem Tarifa" flex={0}>
                                    {Mask.formatCurrency(
                                      responseApi?.total_without_tariff
                                    )}
                                  </Grid.Item>
                                  <Grid.Item name="Tarifa" flex={0}>
                                    {Mask.formatCurrency(responseApi?.tariff)}
                                  </Grid.Item>
                                  <Grid.Item name="Total com Tarifa" flex={0}>
                                    {Mask.formatCurrency(
                                      responseApi?.total_with_tariff
                                    )}
                                  </Grid.Item>
                                </div>
                                <div className="grid grid-cols-3 gap-4 mt-3">
                                  <Grid.Item name="Preço de Venda" flex={0}>
                                    {Mask.formatCurrency(
                                      responseApi?.sale_price
                                    )}
                                  </Grid.Item>
                                  <Grid.Item name="Preço sem Tarifa" flex={0}>
                                    {Mask.formatCurrency(
                                      responseApi?.price_without_tariff
                                    )}
                                  </Grid.Item>
                                  <Grid.Item name="Premiação por Cota" flex={0}>
                                    {Mask.formatCurrency(
                                      responseApi?.commission_by_quote
                                    )}
                                  </Grid.Item>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={8} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {sortedData.length < 1 && (
            <TableRow>
              <TableCell colSpan={10}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
